import { getJwt, HttpError } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

// Shared fetch helper for the playlist endpoints (JWT bearer, JSON in/out, HttpError on non-2xx).
export async function mediaNetworkRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const hasBody = init?.body != null;
    const response = await fetch(`${BASE_URL}/${path}`, {
        ...init,
        headers: {
            'Authorization': `Bearer ${getJwt()}`,
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
            ...(init?.headers ?? {}),
        },
    });

    const contentType = response.headers.get('content-type') ?? '';
    const data = contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) throw new HttpError(response.status, data);
    return data as T;
}

// Uploads a file straight to Azure Blob Storage using the write-only SAS from an upload intent.
//
// This is the ONE request in the portal that does not go to our API, and it must stay that way: routing a
// 250 MB video through PortalAPI would pin an App Service request for its whole transfer and blow the gRPC
// message cap behind it. Consequently NO Authorization header goes here — the SAS is the credential, and
// sending our JWT to a third-party origin would leak it.
//
// Uses XMLHttpRequest rather than fetch purely for upload progress, which fetch still can't report.
// Requires CORS on the storage account for this origin (PUT + the x-ms-blob-* headers); without it this
// fails in the browser only, while every server-side test still passes.
export function uploadToBlob(
    uploadUri: string,
    file: File,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUri, true);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        // Must match the content type the SAS was minted for, or Azure rejects the PUT.
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                onProgress?.(1);
                resolve();
            } else {
                // Azure returns its diagnostics as XML, not JSON, so surface the raw body.
                reject(new HttpError(xhr.status, xhr.responseText || 'Upload failed'));
            }
        };

        xhr.onerror = () => reject(new HttpError(0, 'Upload failed — check storage CORS for this origin.'));
        xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

        signal?.addEventListener('abort', () => xhr.abort(), { once: true });

        xhr.send(file);
    });
}
